import React, {useEffect, useState} from "react";
import {SocialAccount, SocialPlatform, SocialPlatformNames} from "../../../../types/socialTypes";
import {
    createSocialAccount,
    deleteSocialAccount,
    getGroupedAccounts, loginWithFacebook, loginWithGoogle, loginWithInstagram, loginWithTikTok,
    updateSocialAccount
} from "../../../../queries/socialQueries";
import Box from "@mui/material/Box";
import {Link, Typography} from "@mui/material";
import SocialAccountForm from "./SocialAccountForm";
import DailyMotionIcon from "../Icons/Dailymotion"
import InstagramIcon from "../Icons/Instagram"
import FacebookIcon from "../Icons/Facebook"
import YoutubeIcon from "../Icons/Youtube"
import TikTokIcon from '../../../../assets/images/socials/tiktok.svg';
import {socialFormFields} from "../../../../constants/socialConstants";
import Button from "@mui/material/Button";
import { enqueueSnackbar } from 'notistack';
import DividerLabel from "../../CommonComponents/DividerLabel";
import {TelegramAccountForm} from "@/pages/custom-components/SocialPublish/SocialAccounts/TelegramAccountForm";
import TelegramIcon from "@/pages/custom-components/SocialPublish/Icons/Telegram";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import {OP_BUTTON_STYLE} from "@/constants/styles";
import {SOCIAL_ICON_SIZE} from "@/styles/iconHover";

const socialInstructions = {
    [SocialPlatformNames.dailyMotion]: <Link href="https://developers.dailymotion.com/guides/" target="_blank"> documentation</Link>,
    [SocialPlatformNames.instagram]: <Link href="" target="_blank"> documentation</Link>,
    [SocialPlatformNames.facebook]: <Link href="" target="_blank"> documentation</Link>,
    [SocialPlatformNames.youtube]: <Link href="" target="_blank"> documentation</Link>,
    [SocialPlatformNames.tiktok]: <Link href="" target="_blank"> documentation</Link>
}

interface SocialAccountProps {}

const defaultSocialAccount: SocialAccount = {
    id: "",
    name: "",
    account_id: "",
    business_id: "",
    app_id: "",
    page_id: "",
    page_access_token: "",
    client_id: "",
    client_secret: "",
    is_token_valid: false,
    auth_code: "",
    redirect_uri: "",
    social_platform: {
        id: "",
        name: SocialPlatformNames.dailyMotion,
        deletion_support: false,
        social_contents_supported: []
    },
    token: ""
};

const userSocialPlatform = [
    SocialPlatformNames.facebook,
    SocialPlatformNames.instagram,
    SocialPlatformNames.tiktok,
    SocialPlatformNames.youtube,
    SocialPlatformNames.dailyMotion
]

const internalSocialPlatform = [
    SocialPlatformNames.telegram
]

const SocialAccountSection: React.FC<SocialAccountProps> = () => {

    const socialIcons = {
        [SocialPlatformNames.facebook]: <FacebookIcon size={SOCIAL_ICON_SIZE} />,
        [SocialPlatformNames.instagram]: <InstagramIcon size={SOCIAL_ICON_SIZE} />,
        [SocialPlatformNames.tiktok]: <img src={TikTokIcon} style={{width: SOCIAL_ICON_SIZE, height: SOCIAL_ICON_SIZE, marginRight: '5px', color: 'inherit'}} alt="TikTok" /> ,
        [SocialPlatformNames.youtube]: <YoutubeIcon size={SOCIAL_ICON_SIZE} />,
        [SocialPlatformNames.dailyMotion]: <DailyMotionIcon size={SOCIAL_ICON_SIZE} />,
        [SocialPlatformNames.telegram]: <TelegramIcon size={SOCIAL_ICON_SIZE} />
    }

    const [socialPlatforms, setSocialPlatform] = useState<SocialPlatform[]>([])

    async function loadAccounts() {
        try{
            const socialPlatforms = await getGroupedAccounts()
            for (const socialPlatform of socialPlatforms){
                if (socialPlatform.accounts){
                    for (const socialAccount of socialPlatform.accounts){
                        socialAccount.mode = "readonly"
                    }
                }
            }
            setSocialPlatform(socialPlatforms);

        }catch(err){
            console.error(err);
            enqueueSnackbar('Problems loading the socials accounts', { variant: 'error' });
        }
    }

    function blank_secret_fields(socialPlatform: SocialPlatform, socialAccount: SocialAccount){
        const secretSocialAccountFields = socialFormFields[socialPlatform.name].filter((form: any) => form.secret)
        for (const secretField of secretSocialAccountFields){
            (socialAccount[secretField.name as keyof SocialAccount] as any) = "";
        }
    }

    function onChangeFormMode(socialPlatformIndex: number, socialAccountIndex: number, mode: 'readonly' | 'updatable'){
        const tempSocialPlatforms = [...socialPlatforms]
        const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
        if (socialAccounts){
            const socialAccount = socialAccounts[socialAccountIndex] as any
            socialAccount.mode = mode
            blank_secret_fields(tempSocialPlatforms[socialPlatformIndex], socialAccounts[socialAccountIndex])
            setSocialPlatform(tempSocialPlatforms)
        }
    }

    useEffect(() => {
        loadAccounts()
    }, []);

    function handleFieldChange(fieldName: string, value: any, socialPlatformIndex: number, socialAccountIndex: number){
        const tempSocialPlatforms = [...socialPlatforms]
        const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
        if (socialAccounts){
            const socialAccount = socialAccounts[socialAccountIndex] as any
            socialAccount[fieldName] = value
            setSocialPlatform(tempSocialPlatforms)
        }
    }

    async function handleAddAccount(socialPlatformIndex: number) {
        const tempSocialPlatforms = [...socialPlatforms];
        const socialPlatform = tempSocialPlatforms[socialPlatformIndex];
        const socialAccounts = socialPlatform.accounts;

        if (!socialAccounts) return;

        const loginProviders: Record<string, () => Promise<string>> = {
            [SocialPlatformNames.youtube]: loginWithGoogle,
            [SocialPlatformNames.instagram]: loginWithInstagram,
            [SocialPlatformNames.facebook]: loginWithFacebook,
            [SocialPlatformNames.tiktok]: loginWithTikTok,
        };

        const loginFunction = loginProviders[socialPlatform.name];

        if (loginFunction) {
            try {
                const redirectUrl = await loginFunction();
                window.location.replace(redirectUrl);
            } catch (e) {
                console.error(e);
                enqueueSnackbar('Error occurred while logging in', { variant: 'error' });
            }
        } else {
            const socialAccount = JSON.parse(JSON.stringify(defaultSocialAccount)) as SocialAccount;
            socialAccount.social_platform.id = socialPlatform.id;
            socialAccount.social_platform.name = socialPlatform.name;
            socialAccount.mode = "updatable";

            socialAccounts.unshift(socialAccount);
            setSocialPlatform(tempSocialPlatforms);
        }
    }

    async function handleDeleteAccount(socialPlatformIndex:number, socialAccountIndex:number){
        const targetSocialPlatform = socialPlatforms[socialPlatformIndex];
        const socialAccounts = targetSocialPlatform.accounts;
        if (socialAccounts){
            const targetSocialAccount = socialAccounts[socialAccountIndex];
            try {
                const tempSocialPlatforms = [...socialPlatforms];
                const socialPlatform = tempSocialPlatforms.find(sc => sc.id === targetSocialPlatform.id);
                if (socialPlatform) {
                    const currentAccounts = socialPlatform.accounts as SocialAccount[];
                    const indexToDelete = currentAccounts.findIndex(sa => sa.id === targetSocialAccount.id);
                    if(targetSocialAccount.id !== '') await deleteSocialAccount(targetSocialAccount.id);
                    currentAccounts.splice(indexToDelete, 1);
                    setSocialPlatform(tempSocialPlatforms);
                    if(targetSocialAccount.id !== '') enqueueSnackbar("Account Deleted", { variant: 'success' });
                }
            } catch (e) {
                enqueueSnackbar("Account Not Deleted", { variant: 'error' });
            }
        }
    }

    async function handleUpdateAccount(socialPlatformIndex: number, socialAccountIndex: number) {
        try {
            const tempSocialPlatforms = [...socialPlatforms]
            const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
            if(socialAccounts) {
                await updateSocialAccount(socialAccounts[socialAccountIndex].id, socialAccounts[socialAccountIndex]);
                enqueueSnackbar("Account Updated", { variant: 'success' });
            }
        } catch (error) {
            enqueueSnackbar("Account Not Updated", { variant: 'error' });
        }
    }

    async function handleCreateAccount(socialPlatformIndex: number, socialAccountIndex: number) {
        try {
            const tempSocialPlatforms = [...socialPlatforms]
            const socialAccounts = tempSocialPlatforms[socialPlatformIndex].accounts
            if(socialAccounts) {
                const socialAccountId = await createSocialAccount(socialAccounts[socialAccountIndex]);
                const socialAccount = socialAccounts[socialAccountIndex]
                socialAccount.id = socialAccountId.id
                socialAccount.mode = 'readonly'
                blank_secret_fields(tempSocialPlatforms[socialPlatformIndex], socialAccounts[socialAccountIndex])
                setSocialPlatform(tempSocialPlatforms)
                enqueueSnackbar("Account created", { variant: 'success' });
            }
        } catch (errorCreateAccount) {
            enqueueSnackbar("Account creation failed", { variant: 'error' });
        }
    }

    return (
        <Box>
            {socialPlatforms.map((socialPlatform, socialPlatformIndex) => {
                if (userSocialPlatform.includes(socialPlatform.name)){
                    return <Box
                        key={socialPlatform.id}
                        sx={{
                            mb: 4
                        }}
                        data-tour= {socialPlatform.name === 'instagram' ? "social_instagram_wide_box" : "social_wide_box"}
                    >
                        {/* Intestazione: DividerLabel a sinistra/centro e Bottone rimpicciolito all'estrema destra */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} >

                            <Box sx={{ flexGrow: 1 }} >
                                <DividerLabel
                                    label={
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            fontSize: '1.25rem', // Più grande
                                            fontWeight: 'bold',
                                            textTransform: 'capitalize' // Prima lettera maiuscola
                                        }}>
                                        {socialIcons[socialPlatform.name]}
                                            {socialPlatform.name}
                                    </span>
                                    }
                                />
                            </Box>

                            <Button
                                variant="contained"
                                data-tour={socialPlatform.name === 'instagram' ? "social_instagram_add_account" : "social_add_account_overview"}
                                onClick={() => handleAddAccount(socialPlatformIndex)}
                                sx={{ whiteSpace: 'nowrap', width: '150px' }}
                                startIcon={<AddCircleOutlineOutlinedIcon/>}
                            >
                                Add Account
                                {
                                    /*socialPlatform.name === SocialPlatformNames.youtube
                                    ? "Log In with Google"
                                    : socialPlatform.name === SocialPlatformNames.instagram
                                        ? "Log In with Instagram"
                                        : socialPlatform.name === SocialPlatformNames.facebook
                                            ? "Log in With Facebook"
                                            : "Link Account"*/}
                            </Button>
                        </Box>

                        {/* Corpo della sezione (Form e messaggi) */}
                        <Box sx={{ mt: 1 }}>
                            {socialPlatform.accounts && socialPlatform.accounts.map((socialAccount, socialAccountIndex) => {
                                const refreshDate = new Date(socialAccount.refresh_time!);
                                const now = new Date();

                                return (
                                    <Box key={socialAccountIndex} sx={{ mt: 2 }}>
                                        <SocialAccountForm
                                            socialPlatform={socialPlatform.name}
                                            socialAccount={socialAccount}
                                            onFieldChange={(field, value) => handleFieldChange(field, value, socialPlatformIndex, socialAccountIndex)}
                                            onDelete={() => handleDeleteAccount(socialPlatformIndex, socialAccountIndex)}
                                            onCreate={() => handleCreateAccount(socialPlatformIndex, socialAccountIndex)}
                                            onUpdate={() => handleUpdateAccount(socialPlatformIndex, socialAccountIndex)}
                                            onChangeMode={(mode) => onChangeFormMode(socialPlatformIndex, socialAccountIndex, mode)}
                                            mode={socialAccount.mode ? socialAccount.mode : 'readonly'}
                                        />

                                        {socialPlatform.name === SocialPlatformNames.facebook && refreshDate <= now && (
                                            <Typography color="warning.main" sx={{ mt: 1 }}>
                                                Attention! You must log in again as your token has expired.
                                            </Typography>
                                        )}

                                        {socialPlatform.name === SocialPlatformNames.youtube && socialAccount.token && (
                                            <Typography color="warning.main" sx={{ mt: 1 }}>
                                                Attention! You must log in again as your token has expired.
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}

                            <Box sx={{ mt: 2 }}>
                                {(!socialPlatform.accounts || socialPlatform.accounts.length === 0) && (
                                    <Typography color="text.secondary">
                                        No account for this social
                                    </Typography>
                                )}

                                {socialPlatform.accounts &&
                                    socialPlatform.accounts.length > 0 &&
                                    socialPlatform.name === SocialPlatformNames.dailyMotion && (
                                        <Typography color="text.secondary" data-tour={"social_retrieval_dev"}>
                                            {`You don't know how to retrieve this ${socialPlatform.name} info? Try to consult this `}
                                            {socialInstructions[socialPlatform.name]}
                                        </Typography>
                                    )}
                            </Box>
                        </Box>
                    </Box>
                }
            })}
            <Box sx={{ width: '100%' }}>
                <DividerLabel
                    my={1}
                    label={
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            fontSize: '1.25rem', // Più grande
                            fontWeight: 'bold',
                            textTransform: 'capitalize' // Prima lettera maiuscola
                        }}>
                            Messaging Apps
                        </span>
                    }
                />
                <Box>
                    {socialPlatforms.map((socialPlatform, socialPlatformIndex) => {
                        if(internalSocialPlatform.includes(socialPlatform.name)){
                            return <Box
                                key={socialPlatform.id}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                                    <Box sx={{ flexGrow: 1 }}>
                                        <DividerLabel
                                            label={
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    fontSize: '1.25rem', // Più grande
                                                    fontWeight: 'bold',
                                                    textTransform: 'capitalize' // Prima lettera maiuscola
                                                }}>
                                                    {socialIcons[socialPlatform.name]}
                                                    {socialPlatform.name}
                                                </span>
                                            }
                                        />
                                    </Box>
                                </Box>

                                {socialPlatform.accounts && socialPlatform.accounts.map((socialAccount, socialAccountIndex) => (
                                    <TelegramAccountForm socialAccount={socialAccount}/>
                                ))}
                            </Box>

                        }
                    })}
                </Box>
            </Box>
        </Box>
    )
}

export default SocialAccountSection;